'use client';

import Grid from "@mui/material/Grid2";
import {Box, Container, Divider, Typography} from "@mui/material";
import Outlines from "@/src/components/common/outlines/outlines";
import BorderGradientBox from "@/src/components/common/border-gradient-box/border-gradient-box";
import Image from "next/image";
import PostDate from "@/src/components/main-site/posts/post-date/post-date";
import {simpleDateFormatter} from "@/src/utils/dateFormatter";
import PageContent from "@/src/components/journal-site/common/page-content/page-content";
import ReactHtmlParser from "react-html-parser";
import PostKeywords from "@/src/components/common/keywords/keywords";
import CommentsComponent from "@/src/components/common/comments/comment-form";
import ReadingProgress from "@/src/components/main-site/posts/reading-progress/reading-progress";
import SocialIcons from "@/src/components/common/pages/social-icons/social-icons";
import {addHeadingIds} from "@/src/utils/html-parser";
import useSWR from "swr";
import {fetcher} from "@/src/utils/fetch-helpers";
import {notFound} from "next/navigation";
import Loading from "@/src/components/common/loading/loading";

export default function PostComponent({
                                          initialJournalData,
                                          initialPostData,
                                          journalTitle,
                                          postSlug,
                                      }: {
    initialJournalData: any;
    initialPostData: any;
    journalTitle: string;
    postSlug: string;
}) {
    const {
        data: journalResponse,
        isLoading: isJournalLoading
    } = useSWR(`/api/journals?path=${journalTitle}`, fetcher, {
        fallbackData: {success: true, data: [initialJournalData]},
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateIfStale: false,
        revalidateOnReconnect: false,
    });

    const journal = Array.isArray(journalResponse?.data) ? journalResponse.data[0] : journalResponse?.data;

    if (!journal) {
        console.error('Journal Not found');
        notFound();
    }

    const {
        data: postResponse,
        isLoading: isPostLoading
    } = useSWR(`/api/posts?slug=${postSlug}&journalId=${journal?.id}`, fetcher, {
        fallbackData: {success: true, data: initialPostData},
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateIfStale: false,
        revalidateOnReconnect: false,
    });

    const post = Array.isArray(postResponse?.data) ? postResponse.data[0] : postResponse?.data;

    if (!post) {
        notFound();
    }

    if (isJournalLoading || isPostLoading) {
        return (
            <Loading/>
        );
    }

    const modifiedContent = addHeadingIds(post?.body || '');

    return (
        <Box
            sx={{
                paddingLeft: {
                    xs: 1,
                    sm: 1.5
                },
                paddingRight: {
                    xs: 1,
                    sm: 1.5
                }
            }}
        >
            <Grid
                container
                spacing={2}
                py={{
                    xs: 2,
                    sm: 2,
                    md: 4
                }}
            >
                <Grid
                    size={{
                        xs: 12,
                        sm: 12,
                        md: 2
                    }}
                    display={{
                        xs: 'none',
                        sm: 'none',
                        md: 'block'
                    }}
                >
                    <Box
                        position={"sticky"}
                        top={30}
                    >
                        <Outlines
                            content={modifiedContent}
                            hasAbstract={true}
                            hasBody={true}
                        />
                    </Box>
                </Grid>
                <Grid size={{xs: 12, sm: 12, md: 8}}>
                    <Grid
                        container
                        spacing={1}
                        mb={{
                            xs: 1,
                            sm: 1,
                            md: 2
                        }}
                    >
                        {
                            (
                                post?.image_url ||
                                journal?.meta_data?.journal_default_cover_image
                            ) &&
                            <Grid size={{xs: 12, sm: 4, md: 3}}>
                                <BorderGradientBox size={'7.5px'}>
                                    <Image
                                        src={
                                            post?.image_url ||
                                            (
                                                journal?.meta_data?.journal_default_cover_image ?
                                                    `${process.env.NEXT_PUBLIC_SERVICE_URI_CDN_SERVE}${journal.meta_data.journal_default_cover_image}` :
                                                    ''
                                            )
                                        }
                                        alt={post?.image_alt || post?.title || ''}
                                        style={{
                                            width: '100%',
                                            height: 'auto'
                                        }}
                                        width={400}
                                        height={400}
                                    />
                                </BorderGradientBox>
                            </Grid>
                        }
                        <Grid size={{xs: 12, sm: 8, md: 9}}>
                            {
                                post?.title &&
                                <Typography
                                    variant={"h1"}
                                    fontSize={'1.75rem'}
                                    color={"primary"}
                                >
                                    {
                                        post.title
                                    }
                                </Typography>
                            }
                            {
                                post?.subtitle &&
                                <Typography
                                    variant={"h2"}
                                    fontSize={'1rem'}
                                    color={"text.primary"}
                                >
                                    {
                                        post?.subtitle || ''
                                    }
                                </Typography>
                            }
                            {
                                post?.publish_date &&
                                <PostDate
                                    date={
                                        simpleDateFormatter(
                                            post.publish_date
                                        )
                                    }
                                />
                            }
                        </Grid>
                    </Grid>
                    <PageContent>
                        {
                            post?.abstract &&
                            <>
                                <Box
                                    id="page-content-abstract"
                                    color={"text.primary"}
                                >
                                    <Typography
                                        variant={"h3"}
                                        color={"primary"}
                                        fontSize={'1.5rem'}
                                    >
                                        Abstract
                                    </Typography>
                                    {
                                        ReactHtmlParser(
                                            post.abstract
                                        )
                                    }
                                </Box>
                                <Divider
                                    sx={{
                                        borderColor: "common.black",
                                        opacity: .1
                                    }}
                                />
                            </>
                        }
                        {
                            modifiedContent &&
                            <Box
                                id="page-content-body"
                                color={"text.primary"}
                            >
                                {
                                    ReactHtmlParser(
                                        modifiedContent
                                    )
                                }
                            </Box>
                        }
                        {
                            post?.keywords &&
                            <PostKeywords
                                keywords={post?.keywords}
                            />
                        }
                    </PageContent>
                    <CommentsComponent
                        url={post?.comment_url || ''}
                        comments={post?.comments}
                        bgColor="custom.beige"
                    />
                </Grid>
                <Grid
                    py={
                        {
                            xs: 0,
                            sm: 0,
                            md: 4
                        }
                    }
                    size={
                        {
                            xs: 12,
                            sm: 12,
                            md: 2
                        }
                    }
                >
                    <Box
                        position={{
                            xs: "relative",
                            sm: "relative",
                            md: "sticky"
                        }}
                        top={{md: 30}}
                    >
                        <ReadingProgress
                            contentText={post?.body || ''}
                        />
                        <SocialIcons
                            url={`${journal?.journal_url}/posts/${post?.slug}`}
                            title={post?.title || ''}
                        />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )
}